import { Grid } from "@mui/material";
import TextFieldFormControl from "../../field/TextFieldFormControl/TextFieldFormControl";
import { useTranslation } from "react-i18next";
import ItemRecord from "../../other/ItemRecord/ItemRecord";
import AutocompleteField from "../../field/AutocompleteField";
import FloatField from "../../base/FloatField/FloatField";
import PropTypes from "prop-types";
import {
  MATERIAL_MAIN_BRANCH_NAME,
  DEVICE_MAIN_BRANCH_NAME,
  COUNTER_BRANCH_NAME,
} from "../../../helpers/constants";
import { useCallback, useEffect, useState } from "react";
import TransactionOutMaterialRecord from "./TransactionOutMaterialRecord";
import { useSnackbarAlert } from "../../../context/snackbarAlert";
import {
  convertUndefinedValueToZero,
  roundPrice,
} from "../../../helpers/methods";
import useItemService from "../../../services/itemService";
import useEquipmentService from "../../../services/equipmentService";
import useOfferService from "../../../services/offerService";
import { useAsyncFn } from "../../../hooks/useAsync";

const TransactionOutMultiForm = (props) => {
  const { t } = useTranslation();
  const snackbarAlert = useSnackbarAlert();

  const {
    getEmptyItemElement,
    prepareItemDataFromBackendForItemRecordComponent,
  } = useItemService();

  const getEmptyMaterialList = () => {
    let tempMaterial = { item: getEmptyItemElement() };
    if (props.itemType === "equipment") {
      tempMaterial["transaction_amount"] = 1;
    }

    return [tempMaterial];
  };
  const [materials, setMaterials] = useState(getEmptyMaterialList());

  const onChangeByIndex = useCallback((changeObj) => {
    setMaterials((prev) => {
      let tempState = [...prev];
      let material = { ...tempState[changeObj.index] };
      if (
        changeObj.name === "transaction_amount" ||
        changeObj.name === "transaction_price"
      ) {
        material[changeObj.name] = convertUndefinedValueToZero(changeObj.value);
        material["transaction_value"] = roundPrice(
          convertUndefinedValueToZero(material.transaction_amount) *
            convertUndefinedValueToZero(material.transaction_price)
        );
      } else {
        material[changeObj.name] = changeObj.value;
      }
      tempState[changeObj.index] = { ...material };
      return [...tempState];
    });
  }, []);

  const onCleanPartByIndex = useCallback((index) => {
    setMaterials((prev) => {
      let tempState = [...prev];
      let material = { ...tempState[index] };
      material["part"] = undefined;
      material["equipment"] = undefined;
      material["poli_unit"] = undefined;
      material["transaction_price"] = undefined;
      material["transaction_amount"] =
        props.itemType === "equipment" ? 1 : undefined;
      material["transaction_value"] = undefined;
      material["transaction_part_production_date"] = undefined;
      material["transaction_part_production_number"] = undefined;
      tempState[index] = { ...material };
      return [...tempState];
    });
  }, []);

  const onSetPartData = useCallback((partObject, index) => {
    setMaterials((prev) => {
      let tempState = [...prev];
      let material = { ...tempState[index] };
      material["part_unit"] = partObject["part_unit"];
      material["transaction_price"] = parseFloat(
        convertUndefinedValueToZero(partObject["part_price"])
      );
      material["transaction_amount"] = undefined;
      material["transaction_value"] = undefined;
      tempState[index] = { ...material };
      return [...tempState];
    });
  }, []);

  const onSetStockData = useCallback((index, stockData) => {
    setMaterials((prev) => {
      let tempState = [...prev];
      let material = { ...tempState[index] };
      material["stock_availability"] = parseFloat(
        convertUndefinedValueToZero(stockData["stock_availability"])
      );
      material["stock_amount"] = parseFloat(
        convertUndefinedValueToZero(stockData["stock_amount"])
      );
      material["stock_price"] = parseFloat(
        convertUndefinedValueToZero(stockData["stock_price"])
      );

      tempState[index] = { ...material };
      return [...tempState];
    });
  }, []);

  const onSetEquipmentData = useCallback((equipmentObject, index) => {
    setMaterials((prev) => {
      let tempState = [...prev];
      let equipment = { ...tempState[index] };
      equipment["transaction_price"] = parseFloat(
        convertUndefinedValueToZero(equipmentObject["equipment_price_net"])
      );
      equipment["transaction_part_production_date"] =
        equipmentObject["transaction_part_production_date"];
      equipment["transaction_part_production_number"] =
        equipmentObject["transaction_part_production_number"];
      equipment["transaction_amount"] = 1;
      equipment["transaction_value"] =
        equipment["transaction_price"] * equipment["transaction_amount"];
      tempState[index] = { ...equipment };
      return [...tempState];
    });
  }, []);

  const onAddMaterial = useCallback(() => {
    setMaterials((prev) => {
      let tempState = [...prev];
      let tempMaterial = { item: getEmptyItemElement() };
      if (props.itemType === "equipment") {
        tempMaterial["transaction_amount"] = 1;
      }
      tempState.push(tempMaterial);
      return [...tempState];
    });
  }, []);

  const onDeleteLocation = useCallback((index) => {
    setMaterials((prev) => {
      let tempState = [...prev];
      tempState.splice(index, 1);

      return [...tempState];
    });
  }, []);

  const onChangeWarehouse = useCallback((...inputProps) => {
    props.onChangeAutocomplete(...inputProps);
    setMaterials(getEmptyMaterialList());
  });

  useEffect(() => {
    props.setFormValue((tempForm) => ({ ...tempForm, materials: materials }));
  }, [materials]);

  return (
    <Grid
      container
      direction="row"
      justifyContent="flex-start"
      alignItems="flex-start"
      spacing={1}
    >
      <Grid item xs={6}>
        <AutocompleteField
          name={`warehouse`}
          label={t(`form.transaction_form.warehouse`)}
          value={props.formValue[`warehouse`]}
          options={props.allowedWarehouses?.value}
          isObjectOption={true}
          optionLabelKey={"warehouse_name"}
          addNewValue={false}
          onChange={onChangeWarehouse}
          required
          error={props.unfillRequiredFields.includes("warehouse")}
          helperText={
            props.unfillRequiredFields.includes("warehouse") &&
            t("field_required")
          }
        />
      </Grid>
      {props.showTransactionWz && (
        <Grid item xs={6}>
          <TextFieldFormControl
            name="transaction_wz"
            value={props.formValue[`transaction_wz`]}
            label={t("form.transaction_form.transaction_wz")}
            disabled={true}
          />
        </Grid>
      )}
      {props.showTransactionOrder && (
        <Grid item xs={6}>
          <AutocompleteField
            name={`transaction_order`}
            label={t(`form.transaction_form.order`)}
            value={props.formValue[`transaction_order`]}
            options={props.orders}
            isObjectOption={true}
            optionLabelKey={"order_descriptive_name"}
            addNewValue={false}
            onChange={props.onChangeAutocomplete}
            required
            error={props.unfillRequiredFields.includes("warehouse")}
            helperText={
              props.unfillRequiredFields.includes("warehouse") &&
              t("field_required")
            }
          />
        </Grid>
      )}

      {props.showTransactionNote && (
        <Grid item xs={6}>
          <TextFieldFormControl
            name="transaction_note"
            onChange={props.onChange}
            value={props.formValue[`transaction_note`]}
            label={t("form.transaction_form.transaction_note")}
            multiline
            rows={3}
            resize={"vertical"}
          />
        </Grid>
      )}
      {materials?.map((material, index) => {
        return (
          <TransactionOutMaterialRecord
            materialIndex={index}
            material={material}
            showItemRecord={true}
            onChangeByIndex={onChangeByIndex}
            onCleanPart={onCleanPartByIndex}
            onSetPartData={onSetPartData}
            onSetStockData={onSetStockData}
            onSetEquipmentData={onSetEquipmentData}
            poPartList={props.poPartList}
            readOnly={props.readOnly}
            showButtonAddMaterial={index === 0}
            onAddMaterial={onAddMaterial}
            showButtonDeleteMaterial={index !== 0}
            onDeleteLocation={onDeleteLocation}
            itemType={props.itemType}
            showSearchItemButton={props.itemType !== "equipment"}
            warehouse={props.formValue?.warehouse}
            showTransactionAmountError={props.showTransactionAmountError}
          />
        );
      })}
    </Grid>
  );
};

TransactionOutMultiForm.propTypes = {
  formValue: PropTypes.object,
  onChange: PropTypes.func,
  onChangeAutocomplete: PropTypes.func,
  readOnly: PropTypes.bool,
  transactionSelectData: PropTypes.array,
  poPartList: PropTypes.array,
  showTransactionWz: PropTypes.bool,
  showTransactionOrder: PropTypes.bool,
  showTransactionNote: PropTypes.bool,
  unfillRequiredFields: PropTypes.array,
  showTransactionAmountError: PropTypes.bool,
};

TransactionOutMultiForm.defaultProps = {
  readOnly: false,
  showTransactionWz: true,
  showTransactionOrder: true,
  showTransactionNote: true,
  unfillRequiredFields: [],
  showTransactionAmountError: false,
};

export default TransactionOutMultiForm;
